import React from 'react';
import ReactDOM from 'react-dom';
import { makeObservable, observable, action, computed, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { ImgMic } from './Icons/ImgMic';

type VoiceRecorderProps = {
  language?: string;
  onTranscription: (text: string) => void;
  onStartRecording?: () => void;
  onRecordingChange?: (isRecording: boolean) => void;
};

@observer
class VoiceRecorder extends React.Component<VoiceRecorderProps> {
  @observable private _isRecording = false;
  @observable private _isProcessing = false;
  @observable private _transcription = '';
  @observable private _error = '';
  @observable private _showErrorPopup = false;
  @observable private _isRequestingPermission = false;
  @observable private _volumeHistory: number[] = [];
  @observable private _currentVolume: number = 0;

  private _containerRef = React.createRef<HTMLDivElement>();
  private _mediaRecorder: MediaRecorder | null = null;
  private _audioChunks: Blob[] = [];
  private _errorTimeout: number | null = null;
  private _abortController: AbortController | null = null;
  private _audioContext: AudioContext | null = null;
  private _analyser: AnalyserNode | null = null;
  private _animationFrameId: number | null = null;
  private _volumeSampleInterval: number | null = null;
  private _lastSoundTime: number = 0;
  private _silenceThreshold: number = 0.05;

  constructor(props: VoiceRecorderProps) {
    super(props);
    makeObservable(this);
  }

  componentDidMount() {
    window.addEventListener('keydown', this._onKeyDown);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this._onKeyDown);
    this._cleanup();
    if (this._errorTimeout) {
      clearTimeout(this._errorTimeout);
    }
    this._stopAudioAnalysis();
  }

  @action.bound
  private _onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this._isRecording) {
      e.preventDefault();
      this._cancelRecording();
    }
  };

  @computed get statusClass(): string {
    if (this._error) return 'error-text';
    if (this._transcription && !this._isRecording && !this._isProcessing) {
      return 'success-text';
    }
    return '';
  }

  @action.bound
  private _setError(message: string) {
    this._error = message;
    this._showErrorPopup = true;

    if (this._errorTimeout) {
      clearTimeout(this._errorTimeout);
    }

    this._errorTimeout = window.setTimeout(() => {
      runInAction(() => {
        this._error = '';
        this._showErrorPopup = false;
      });
    }, 2000);
  }

  @action.bound
  private _setTranscription(value: string) {
    this._transcription = value;
    this.props.onTranscription(value);
  }

  @action.bound
  private _cleanup() {
    if (this._mediaRecorder && this._mediaRecorder.state !== 'inactive') {
      this._mediaRecorder.stop();
    }
    if (this._mediaRecorder?.stream) {
      this._mediaRecorder.stream.getTracks().forEach(track => track.stop());
    }
    this._mediaRecorder = null;
    this._audioChunks = [];

    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }

    this._stopAudioAnalysis();
  }

  @action.bound
  private _stopAudioAnalysis() {
    if (this._animationFrameId) {
      cancelAnimationFrame(this._animationFrameId);
      this._animationFrameId = null;
    }
    if (this._volumeSampleInterval) {
      clearInterval(this._volumeSampleInterval);
      this._volumeSampleInterval = null;
    }
    if (this._audioContext) {
      this._audioContext.close();
      this._audioContext = null;
    }
    this._analyser = null;
    runInAction(() => {
      this._volumeHistory = [];
      this._currentVolume = 0;
    });
  }

  @action.bound
  private _startAudioAnalysis(stream: MediaStream) {
    try {
      this._audioContext = new AudioContext();
      this._analyser = this._audioContext.createAnalyser();
      const source = this._audioContext.createMediaStreamSource(stream);

      this._analyser.fftSize = 256;
      this._analyser.smoothingTimeConstant = 0.8;
      source.connect(this._analyser);

      const bufferLength = this._analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      this._lastSoundTime = Date.now();

      const analyzeVolume = () => {
        if (!this._analyser || !this._isRecording) return;

        this._analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / bufferLength;
        const normalized = average / 255;

        // Auto-stop after 3 seconds of silence
        if (normalized > this._silenceThreshold) {
          this._lastSoundTime = Date.now();
        } else {
          const silenceDuration = Date.now() - this._lastSoundTime;
          if (silenceDuration > 3000) {
            this._stopRecording();
            return;
          }
        }

        runInAction(() => {
          this._currentVolume = normalized;
        });

        this._animationFrameId = requestAnimationFrame(analyzeVolume);
      };

      analyzeVolume();

      // Sample volume for history every 100ms (≈100 samples visible)
      this._volumeSampleInterval = window.setInterval(() => {
        runInAction(() => {
          this._volumeHistory.push(this._currentVolume);
          if (this._volumeHistory.length > 100) {
            this._volumeHistory.shift();
          }
        });
      }, 100);

    } catch (err) {
      console.error('Error setting up audio analysis:', err);
    }
  }

  @action.bound
  async _startRecording() {
    this._error = '';
    this._showErrorPopup = false;
    this._volumeHistory = [];
    this._currentVolume = 0;
    this.props.onStartRecording?.();
    this.props.onRecordingChange?.(true);

    // Provide haptic feedback if supported
    await new Promise<void>(resolve => {
      if (navigator.vibrate) {
        navigator.vibrate([100, 50, 100]);
        setTimeout(resolve, 250);
      } else {
        resolve();
      }
    });

    // Check if recording was cancelled during haptic feedback
    if (!this._isRecording) {
      return;
    }

    try {
      runInAction(() => {
        this._isRequestingPermission = true;
      });

      const constraints = {
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          channelCount: 1,
          volume: 1.0
        },
        video: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      runInAction(() => {
        this._isRequestingPermission = false;
      });

      // Check if recording was cancelled while requesting permission
      if (!this._isRecording) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm'
      });
      this._mediaRecorder = mediaRecorder;
      this._audioChunks = [];

      // Start audio analysis for visualization + auto-stop
      this._startAudioAnalysis(stream);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this._audioChunks.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());

        // Only send to Whisper if we have audio data and aren't already processing
        if (this._audioChunks.length > 0 && !this._isProcessing) {
          const audioBlob = new Blob(this._audioChunks, { type: 'audio/webm' });
          await this.sendToWhisper(audioBlob);
        }
      };

      mediaRecorder.start();

    } catch (err) {
      runInAction(() => {
        this._isRecording = false;
        this._isRequestingPermission = false;
        this._isProcessing = false;
      });

      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        this._setError('Microphone permission denied');
      } else if (err instanceof DOMException && err.name === 'NotFoundError') {
        this._setError('No microphone found');
      } else {
        this._setError(err instanceof Error ? err.message : 'Error accessing microphone');
      }
      console.error('Error accessing microphone:', err);
    }
  }

  @action.bound
  private _stopRecording() {
    if (this._mediaRecorder && this._isRecording) {
      this._mediaRecorder.stop();
      this._isRecording = false;
      this.props.onRecordingChange?.(false);
    }

    navigator.vibrate?.([100]);
  }

  @action.bound
  private _cancelRecording() {
    if (!this._isRecording) return;

    // Clear audio chunks FIRST to prevent any processing
    this._audioChunks = [];

    // Stop the recorder without processing
    if (this._mediaRecorder) {
      this._mediaRecorder.onstop = null;
      if (this._mediaRecorder.state !== 'inactive') {
        this._mediaRecorder.stop();
      }
    }

    this._cleanup();

    runInAction(() => {
      this._isRecording = false;
    });

    this.props.onRecordingChange?.(false);
    navigator.vibrate?.([50, 50, 50]);
  }

  @action.bound
  private _cancelProcessing() {
    if (this._isProcessing) {
      if (this._abortController) {
        this._abortController.abort();
      }

      this._cleanup();

      runInAction(() => {
        this._isProcessing = false;
        this._transcription = '';
      });

      navigator.vibrate?.([50, 50, 50]);
    }
  }

  @action
  async sendToWhisper(audioBlob: Blob) {
    this._isProcessing = true;
    this.props.onRecordingChange?.(false);

    try {
      this._abortController = new AbortController();

      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');
      formData.append('model', 'whisper-1');
      formData.append('content_type', 'audio/webm');

      const lang = this.props.language || 'auto';

      const response = await fetch(`/api/transcribe?lang=${encodeURIComponent(lang)}`, {
        method: 'POST',
        body: formData,
        signal: this._abortController.signal
      });

      if (!response.ok) {
        throw new Error('Transcription failed');
      }

      const data = await response.json();

      runInAction(() => {
        this._transcription = data.text || data.transcription || '';
        this._setTranscription(this._transcription);
        this._isProcessing = false;
      });

    } catch (err) {
      // Don't show error if request was aborted (user cancelled)
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }

      this._setError('Failed to transcribe audio. Configure your backend endpoint.');
      console.error('Transcription error:', err);
    } finally {
      this._abortController = null;
      runInAction(() => {
        this._isProcessing = false;
      });
    }
  }

  @action.bound
  private _onButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (this._isProcessing) {
      this._cancelProcessing();
      return;
    }

    if (this._isRecording) {
      this._stopRecording();
    } else {
      runInAction(() => {
        this._isRecording = true;
      });
      this._startRecording();
    }
  }

  render() {
    const isSupported = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
      && MediaRecorder.isTypeSupported('audio/webm');

    if (!isSupported) {
      return null;
    }

    // Build visualizer bars
    const maxInHistory = this._volumeHistory.length > 0
      ? Math.max(...this._volumeHistory, 0.2)
      : 0.2;
    const LOW_VOLUME_THRESHOLD = 0.15; // normalized bar height fraction below which bars are gray

    // Find the sibling .panel-content to portal the overlay into
    const panelContentEl = this._isRecording && this._containerRef.current
      ? (this._containerRef.current.closest('.translation-panel')?.querySelector('.panel-content') ?? null)
      : null;

    return (
      <div ref={this._containerRef} className="voice-recorder-container">
        <style>{`
          .voice-recorder-container {
            position: relative;
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: center;
          }

          .voice-recorder-overlay {
            position: absolute;
            inset: 0;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            background: var(--bg-secondary);
            z-index: 10;
            padding: 20px;
            border-radius: 0 0 8px 8px;
          }

          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.15); opacity: 0.8; }
          }

          @keyframes ripple {
            0% { transform: scale(1); opacity: 0.6; }
            100% { transform: scale(2.5); opacity: 0; }
          }

          @keyframes wave {
            0% { transform: scale(1); opacity: 0.6; }
            100% { transform: scale(2); opacity: 0; }
          }

          @keyframes colorShift {
            0%, 100% { border-color: #ff9500; }
            25% { border-color: #ff6b00; }
            50% { border-color: #ffa800; }
            75% { border-color: #ff8500; }
          }

          @keyframes slideDown {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }

          @keyframes fadeOut {
            from { opacity: 1; }
            to { opacity: 0; }
          }

          @keyframes barInit {
            from { opacity: 0; transform: scaleY(0); }
            to { opacity: 1; transform: scaleY(1); }
          }

          .voice-button {
            position: relative;
            width: 30px;
            height: 30px;
            border-radius: 50%;
            border: 2px solid var(--border-color);
            background: var(--bg-secondary);
            color: var(--text-primary);
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
            user-select: none;
            -webkit-user-select: none;
            flex-shrink: 0;
          }

          .audio-visualizer {
            display: flex;
            align-items: flex-end;
            justify-content: center;
            gap: 1px;
            flex: 1;
            width: 100%;
            height: 100%;
            max-height: 140px;
            padding: 0 20px;
            overflow: hidden;
            opacity: 0;
            transform: scaleY(0);
            transition: opacity 0.4s ease, transform 0.4s ease;
          }

          .audio-visualizer.active {
            opacity: 1;
            transform: scaleY(1);
          }

          .audio-bar {
            flex: 1;
            min-width: 2px;
            max-width: 5px;
            border-radius: 2px;
            transition: height 0.1s ease-out, background 0.2s ease;
            transform-origin: bottom;
            animation: barInit 0.6s ease-out forwards;
          }

          .audio-bar.bar-accent {
            background: linear-gradient(to top, var(--accent-color), #ff6b00);
          }

          .audio-bar.bar-gray {
            background: var(--border-color, #888);
          }

          .voice-button:hover {
            background: var(--hover-bg);
            border-color: var(--accent-color);
          }

          .voice-button:active {
            transform: scale(0.95);
          }

          .voice-button.recording {
            background: var(--accent-color);
            border-color: var(--accent-color);
            animation: pulse 1.5s ease-in-out infinite;
          }

          .voice-button.processing {
            background: transparent;
            border-color: #ff9500;
            border-width: 3px;
            cursor: pointer;
            animation: colorShift 2s ease-in-out infinite;
          }

          .voice-button.processing:hover {
            background: rgba(255, 149, 0, 0.1);
          }

          .voice-button.error {
            background: #ff4444;
            border-color: #ff4444;
            animation: pulse 0.5s ease-in-out 2;
          }

          .voice-button.requesting-permission {
            border-color: #ffaa00;
            cursor: wait;
            opacity: 0.7;
          }

          .ripple-container {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            overflow: visible;
          }

          .ripple {
            position: absolute;
            top: 50%;
            left: 50%;
            width: 44px;
            height: 44px;
            margin-left: -22px;
            margin-top: -22px;
            border-radius: 50%;
            border: 2px solid var(--accent-color);
            animation: ripple 1.5s ease-out infinite;
          }

          .ripple:nth-child(2) { animation-delay: 0.5s; }
          .ripple:nth-child(3) { animation-delay: 1s; }

          .processing-wave {
            position: absolute;
            top: 50%;
            left: 50%;
            width: 30px;
            height: 30px;
            margin-left: -15px;
            margin-top: -15px;
            border-radius: 50%;
            border: 2px solid #ff9500;
            pointer-events: none;
          }

          .processing-wave:nth-child(1) { animation: wave 1.2s ease-out infinite; }
          .processing-wave:nth-child(2) { animation: wave 1.2s ease-out infinite 0.3s; }
          .processing-wave:nth-child(3) { animation: wave 1.2s ease-out infinite 0.6s; }
          .processing-wave:nth-child(4) { animation: wave 1.2s ease-out infinite 0.9s; }

          .prompt-container {
            display: flex;
            gap: 8px;
            align-items: center;
            justify-content: center;
          }

          .cancel-recording-btn {
            position: absolute;
            right: 20px;
            bottom: 20px;
            width: 30px;
            height: 30px;
            padding: 6px;
            background: var(--bg-secondary);
            border: 2px solid var(--border-color);
            border-radius: 50%;
            color: var(--text-secondary);
            cursor: pointer;
            font-size: 12px;
            font-weight: 500;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            z-index: 100;
          }

          .cancel-recording-btn:hover {
            background: rgba(255, 68, 68, 0.1);
            border-color: #ff4444;
            color: #ff4444;
            transform: scale(1.05);
            box-shadow: 0 4px 12px rgba(255, 68, 68, 0.2);
          }

          .cancel-recording-btn:active {
            transform: scale(0.98);
          }

          .cancel-icon {
            width: 12px;
            height: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            line-height: 1;
          }

          .error-popup {
            position: absolute;
            bottom: calc(100% + 12px);
            left: 50%;
            transform: translateX(-50%);
            background: #ff4444;
            color: white;
            padding: 8px 16px;
            border-radius: 8px;
            font-size: 13px;
            white-space: nowrap;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            animation: slideDown 0.2s ease-out;
            z-index: 1000;
          }

          .error-popup::after {
            content: '';
            position: absolute;
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            border: 6px solid transparent;
            border-top-color: #ff4444;
          }

          .error-popup.hiding {
            animation: fadeOut 0.2s ease-out forwards;
          }
        `}</style>

        {this._isRecording && panelContentEl && ReactDOM.createPortal(
          <div className="voice-recorder-overlay">
            <div className="audio-visualizer active">
              {(this._volumeHistory.length > 0 ? this._volumeHistory : Array(100).fill(0.08)).map((volume, index) => {
                const isPlaceholder = this._volumeHistory.length === 0;
                const totalBars = this._volumeHistory.length > 0 ? this._volumeHistory.length : 100;

                let height: number;
                let isLow: boolean;

                if (isPlaceholder) {
                  height = 5 + Math.sin(index * 0.2) * 3;
                  isLow = true;
                } else {
                  const threshold = 0.03;
                  const adjustedVolume = volume < threshold ? 0 : volume - threshold;
                  const normalized = adjustedVolume / Math.max(maxInHistory - threshold, 0.1);
                  const scaledVolume = Math.pow(normalized, 0.6);
                  height = Math.max(5, scaledVolume * 100);
                  isLow = (height / 100) < LOW_VOLUME_THRESHOLD;
                }

                return (
                  <div
                    key={index}
                    className={`audio-bar ${isLow ? 'bar-gray' : 'bar-accent'}`}
                    style={{
                      height: `${height}%`,
                      opacity: isPlaceholder ? 0.25 : 0.4 + (index / totalBars) * 0.6,
                      animationDelay: isPlaceholder ? `${index * 0.01}s` : '0s'
                    }}
                  />
                );
              })}
            </div>

            <button
              className="cancel-recording-btn"
              onClick={this._cancelRecording}
              title="Cancel recording (Esc)"
            >
              <span className="cancel-icon">✕</span>
            </button>
          </div>,
          panelContentEl
        )}

        <div className="prompt-container">
          <button
            className={`voice-button ${this._isRecording ? 'recording' : ''} ${this._isProcessing ? 'processing' : ''} ${this._error ? 'error' : ''} ${this._isRequestingPermission ? 'requesting-permission' : ''}`}
            onClick={this._onButtonClick}
            disabled={this._isRequestingPermission}
            title={
              this._isProcessing
                ? 'Transcribing... Click to cancel'
                : this._isRecording
                ? 'Recording... Click to stop'
                : this._isRequestingPermission
                ? 'Requesting microphone permission...'
                : 'Click to record'
            }
          >
            <ImgMic />
            {this._isRecording && (
              <div className="ripple-container">
                <div className="ripple" />
                <div className="ripple" />
                <div className="ripple" />
              </div>
            )}
            {this._isProcessing && (
              <div className="ripple-container">
                <div className="processing-wave" />
                <div className="processing-wave" />
                <div className="processing-wave" />
                <div className="processing-wave" />
              </div>
            )}
          </button>

          {this._showErrorPopup && this._error && (
            <div className="error-popup">
              {this._error}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default VoiceRecorder;